import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { localizedPath, findRouteFromPath } from '../utils/routes';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const { language, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const { lang } = useParams();
  const currentLang = lang || language;

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const handleLogout = () => { logout(); navigate(`/${currentLang}`); };

  const handleToggleLanguage = () => {
    const newLang = currentLang === 'en' ? 'fr' : 'en';
    const routeName = findRouteFromPath(location.pathname);
    if (routeName) {
      navigate(localizedPath(routeName, newLang) + location.search + location.hash);
    } else {
      navigate(location.pathname.replace(/^\/(fr|en)/, `/${newLang}`) + location.search + location.hash);
    }
  };

  const lp = (routeName) => localizedPath(routeName, currentLang);

  const linkStyle = (active) => ({
    color: active ? '#D42B2B' : '#94A3B8', textDecoration: 'none',
    fontFamily: "'Barlow', sans-serif", fontSize: 'clamp(0.7rem, 0.8vw, 0.85rem)', letterSpacing: '0.1em',
    textTransform: 'uppercase', fontWeight: 500,
    borderBottom: active ? '1px solid #D42B2B' : '1px solid transparent',
    paddingBottom: '2px', transition: 'all 0.3s',
  });

  const mobileLink = (active) => ({
    color: active ? '#D42B2B' : '#94A3B8', textDecoration: 'none',
    padding: '0.5rem 0',
  });

  const bookBtn = {
    background: 'transparent', border: '1px solid #D42B2B', color: '#D42B2B',
    padding: '0.6rem 1.5rem', fontFamily: "'Barlow', sans-serif",
    fontSize: 'clamp(0.65rem, 0.75vw, 0.8rem)', letterSpacing: '0.1em', textTransform: 'uppercase',
    cursor: 'pointer', transition: 'all 0.3s', textDecoration: 'none',
  };

  const langToggle = {
    background: 'transparent', border: '1px solid rgba(148,163,184,0.3)',
    color: '#94A3B8', padding: '0.5rem 1rem',
    fontFamily: "'Barlow', sans-serif", fontSize: 'clamp(0.65rem, 0.75vw, 0.8rem)',
    letterSpacing: '0.08em', cursor: 'pointer', transition: 'all 0.3s', fontWeight: 500,
  };

  const navLinks = [
    ['home', t.nav.home],
    ['services', t.nav.services],
    ['about', t.nav.about],
    ['contact', t.nav.contact],
    ['faq', t.nav.faq],
  ];

  return (
    <>
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: menuOpen ? 1003 : 1000,
        padding: scrolled ? '0.8rem clamp(1rem, 3vw, 3rem)' : '1.2rem clamp(1rem, 3vw, 3rem)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: scrolled ? 'rgba(12,18,32,0.97)' : 'transparent',
        borderBottom: scrolled ? '1px solid rgba(148,163,184,0.2)' : 'none',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        transition: 'all 0.4s ease',
      }}>
        <Link to={`/${currentLang}`} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <img src="/logo.webp" alt="Mécanique Auto CJPA" className={`cjpa-logo${scrolled ? ' scrolled' : ''}`} />
        </Link>

        {/* Desktop nav links */}
        <ul className="cjpa-nav-links" style={{ display: 'flex', alignItems: 'center', gap: 'clamp(1rem, 2vw, 2.5rem)', listStyle: 'none', margin: 0, padding: 0 }}>
          {navLinks.map(([routeName, label]) => {
            const path = lp(routeName);
            return (
              <li key={routeName}><Link to={path} style={linkStyle(location.pathname === path)}>{label}</Link></li>
            );
          })}
          {user ? (
            <>
              <li><Link to={lp('dashboard')} style={linkStyle(location.pathname === lp('dashboard'))}>{t.nav.myAccount}</Link></li>
              {user.role === 'admin' && <li><Link to={lp('admin')} style={linkStyle(location.pathname === lp('admin'))}>{t.nav.admin}</Link></li>}
              <li><button onClick={handleLogout} style={{...bookBtn, cursor:'pointer'}}>{t.nav.signOut}</button></li>
            </>
          ) : (
            <>
              <li><Link to={lp('login')} style={linkStyle(location.pathname === lp('login'))}>{t.nav.signIn}</Link></li>
              <li><Link to={lp('book')} style={bookBtn}>{t.nav.bookNow}</Link></li>
            </>
          )}
          {user && <li><Link to={lp('book')} style={bookBtn}>{t.nav.bookNow}</Link></li>}
          <li>
            <button onClick={handleToggleLanguage} style={langToggle}>
              {currentLang === 'en' ? 'EN | FR' : 'FR | EN'}
            </button>
          </li>
        </ul>

        {/* Hamburger */}
        <button className={`cjpa-hamburger${menuOpen ? ' open' : ''}`} onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu">
          <span /><span /><span />
        </button>
      </nav>

      {/* Mobile overlay menu */}
      <div className={`cjpa-mobile-overlay${menuOpen ? ' open' : ''}`}>
        {navLinks.map(([routeName, label]) => {
          const path = lp(routeName);
          return (
            <Link key={routeName} to={path} style={mobileLink(location.pathname === path)}>{label}</Link>
          );
        })}
        {user ? (
          <>
            <Link to={lp('dashboard')} style={mobileLink(location.pathname === lp('dashboard'))}>{t.nav.myAccount}</Link>
            {user.role === 'admin' && <Link to={lp('admin')} style={mobileLink(location.pathname === lp('admin'))}>{t.nav.admin}</Link>}
            <button onClick={handleLogout} style={{ ...bookBtn, marginTop: '0.5rem' }}>{t.nav.signOut}</button>
          </>
        ) : (
          <Link to={lp('login')} style={mobileLink(location.pathname === lp('login'))}>{t.nav.signIn}</Link>
        )}
        <Link to={lp('book')} style={{ ...bookBtn, marginTop: '0.5rem' }}>{t.nav.bookNow}</Link>
        <button onClick={handleToggleLanguage} style={{ ...langToggle, marginTop: '0.5rem' }}>
          {currentLang === 'en' ? 'EN | FR' : 'FR | EN'}
        </button>
      </div>
    </>
  );
}
